import * as React from 'react'
import { withPrefix } from 'gatsby';
import { useTopNavigation } from '../../queries/top-navigation';
import SideNav from '../side-nav/side-nav';

import * as layoutStyles from './telerik-layout.module.scss'
import { LocationContext } from '../../hooks/location';
import { PageDataContext } from '../../hooks/page-data';
import { AnnouncementBannerHeightContext } from '../../hooks/announcement-banner-height';
import { PreferredLanguageContext } from '../../hooks/preferred-language-context';

import AnnouncementBanner from '../announcement-banner/announcement-banner';
import GoogleTagManager from '../google-tag-manager/google-tag-manager';
import SearchBar from '../search-bar/search-bar';
import { CurrentNavSegmentsContext, useNavMap } from '../../hooks/current-nav-segments';
import ArticleToc from '../article-toc/article-toc';
import BreadCrumb from '../breadcrumb/breadcrumb'
import { PremiumGradient } from '../premium-tag/premium-tag';
import classNames from '../../utilities/class-names';
import { AiStarsGradient } from '../icons/ai-stars';
import { getScrollPadding } from '../../utilities/get-scroll-padding';
import AgentForceChat from '../agent-force-chat/agent-force-chat';
import { useSiteMetadata } from '../../queries/site-medatata';

let fragment = null;

const navHeight = 60;
const searchBarHeight = 60;
const preferredLanguageKey = "Selected_TabStrip_Language_Key"

const TelerikLayout = ({ children, location, data, pageContext }) => {
    const headref = React.useRef(null);
    const topNavigation = useTopNavigation();

    const [bannerHeight, setBannerHeight] = React.useState(0);
    const [preferredLanguageName, setPreferredLanguageName] = React.useState(null);

    const navMap = useNavMap();
    const currentNavSegments = React.useMemo(() => {
        const segments = navMap[location.pathname];

        if (segments) {
            return segments;
        }

        const parentNavUrl = pageContext?.parentNavUrl;

        if (parentNavUrl) {
            const parentSegments = navMap[withPrefix(parentNavUrl)] || [];

            if (parentSegments.length > 0) {
                return [{ ...parentSegments[0], hideChildren: false }, ...parentSegments.slice(1)];
            }

            return parentSegments;
        }
        
        return [];
    }, [navMap, location.pathname, pageContext?.parentNavUrl]);

    const siteMetadata = useSiteMetadata();

    React.useEffect(() => {
        const lang = localStorage?.getItem(preferredLanguageKey);
        setPreferredLanguageName(lang);
    }, []);

    React.useEffect(() => {
        document.documentElement.style.setProperty('--banner-height', `${bannerHeight}px`);
        return () => document.documentElement.style.removeProperty('--banner-height');
    }, [bannerHeight]);

    const languageContext = React.useMemo(() => {
        return {
            preferredLanguageName: preferredLanguageName,
            setPreferredLanguageName: (value) => {
                localStorage?.setItem(preferredLanguageKey, value);
                setPreferredLanguageName(value);
            }
        }
    }, [preferredLanguageName]);


    React.useEffect(() => {
        if (!process.env.GATSBY_DEV) {
            return;
        }

        //// In dev mode there is no server rendering and the innerHtml set does not execute scripts
        //// furthermore the 404 page seems to reinit the navigation (due to an unfixed bug) so we need to reinit the nav (despite it already being loaded)
        window.TLRK_NAV_INITIALIZED = false;
        fragment = document.createRange().createContextualFragment(headref.current.innerHTML.replace('.mjs', '.mjs?' + new Date().getTime()));
        headref.current.innerHTML = '';
        headref.current.appendChild(fragment);
    }, []);

    React.useEffect(() => {
        const initial = getScrollPadding();

        window.document.documentElement.style.setProperty('--nav-height', `${navHeight}px`);
        window.document.documentElement.style.setProperty('--tlrk-search-bar-height', `${searchBarHeight}px`);

        const newScrollPadding = getScrollPadding();

        const scrollPaddingDiff = newScrollPadding - initial;

        const hash = window.location.hash;
        const hashTarget = hash ? document.getElementById(hash.substring(1)) : null;

        if (scrollPaddingDiff === 0 || !hashTarget) {
            return;
        }

        let timeoutId = null;

        setTimeout(() => {
            hashTarget.scrollIntoView();
        }, 0);

        return () => {
            if (timeoutId) {
                clearTimeout(timeoutId);
            }
        };
    }, []);

    const hideToc = React.useMemo(() => data?.markdownRemark?.fields?.hideToc || false, [data]);
    const fullscreen = React.useMemo(() => data?.markdownRemark?.frontmatter?.fullscreen || data?.aiChatPage || false, [data]);
    const hideBreadCrumb = React.useMemo(() => fullscreen, [fullscreen]);
    const landingPage = React.useMemo(() => data?.markdownRemark?.frontmatter?.landing_page || false, [data]);

    return (
        <CurrentNavSegmentsContext.Provider value={currentNavSegments}>
            <PreferredLanguageContext.Provider value={languageContext}>
                <PageDataContext.Provider value={data}>
                    <LocationContext.Provider value={location}>
                        <AnnouncementBannerHeightContext.Provider value={bannerHeight}>
                            <GoogleTagManager />
                            <PremiumGradient></PremiumGradient>
                            <AiStarsGradient></AiStarsGradient>
                            <AnnouncementBanner bannerHeightChanged={setBannerHeight} />
                            <span className={bannerHeight > 0 ? `bannerActive` : ''} ref={headref} dangerouslySetInnerHTML={{ __html: topNavigation.headerMarkup }}></span>
                            <SearchBar topPx={navHeight} />
                            <div
                                className={`d-flex app-body ${fullscreen ? layoutStyles.fullscreen : ''}`}
                                style={{ 
                                    minHeight: `calc(100vh - ${navHeight + searchBarHeight}px)`, }
                                }>
                                <div className={layoutStyles.nav + ' nav-container d-print-none'}>
                                    <SideNav topPx={navHeight + searchBarHeight} />
                                </div>
                                <div className={`main-container u-mw0 flex-grow-1 ${hideToc ? layoutStyles.tocHidden : ''}`}>
                                    <div className='d-flex flex-column h-100'>
                                        <div className={`e2e-content flex-grow-1 d-flex ${layoutStyles.main}`}>
                                            <div className={`${layoutStyles.body} ${landingPage ? layoutStyles.landingPage : ''}`.trimEnd()} >
                                                {!hideBreadCrumb && <BreadCrumb></BreadCrumb>}
                                                {children}
                                            </div>
                                            <div className={`d-none d-xl-block ${layoutStyles.toc}`}>
                                                <div className={classNames("e2e-right-nav", layoutStyles.rightNav)}>
                                                    {
                                                        !hideToc && !fullscreen && <ArticleToc key={location.pathname}></ArticleToc>
                                                    }
                                                </div>
                                            </div>
                                        </div>
                                        <div className='d-print-none' dangerouslySetInnerHTML={{ __html: topNavigation.footerMarkup }}></div>
                                    </div>
                                </div>
                            </div>
                            { siteMetadata.showAgentForceChat && <AgentForceChat></AgentForceChat> }
                        </AnnouncementBannerHeightContext.Provider>
                    </LocationContext.Provider>
                </PageDataContext.Provider>
            </PreferredLanguageContext.Provider>
        </CurrentNavSegmentsContext.Provider>);
}

export default TelerikLayout;